import { Suspense, lazy } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button, Affix } from "@mantine/core";
import { UserPlus } from "lucide-react";
import LandingNavbar from "../components/landing/LandingNavbar";
import LandingHero from "../components/landing/LandingHero";
import TrustStrip from "../components/landing/TrustStrip";
import OptionCards from "../components/landing/OptionCards";
import ServicesSection from "../components/landing/ServicesSection";
import Reveal from "../components/landing/Reveal";
import { CONTACT } from "../components/landing/data/landingData";
import "../components/landing/landing.css";
import "../components/landing/landing-sections.css";

// Below-the-fold sections load on demand
const CityGrid = lazy(() => import("../components/landing/CityGrid"));
const HowItWorks = lazy(() => import("../components/landing/HowItWorks"));
const AppPromo = lazy(() => import("../components/landing/AppPromo"));
const BlogSection = lazy(() => import("../components/landing/BlogSection"));
const SocialCards = lazy(() => import("../components/landing/SocialCards"));
const CtaBanner = lazy(() => import("../components/landing/CtaBanner"));
const LandingFooter = lazy(() => import("../components/landing/LandingFooter"));

const SITE_URL = (typeof window !== "undefined" && window.location.origin) || "https://allhomedesire.com";

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "All Home Desire",
    url: SITE_URL,
    email: CONTACT.email,
    telephone: CONTACT.phone,
    address: { "@type": "PostalAddress", streetAddress: CONTACT.address, addressCountry: "IN" },
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "All Home Desire",
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/property-list?city={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  },
];

const TITLE = "Find Your Dream Property - Buy, Rent & Sell with All Home Desire";
const DESCRIPTION =
  "Discover verified homes, villas and commercial properties across India with All Home Desire, a modern real estate platform to buy, rent and sell.";

function HomePage() {
  return (
    <div className="ahd-landing">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={`${SITE_URL}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="All Home Desire" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Affix position={{ bottom: 28, right: 28 }} zIndex={100}>
        <Button
          component={Link}
          to="/partner/signup"
          size="md"
          radius="xl"
          leftSection={<UserPlus size={18} />}
          style={{
            backgroundColor: "#2563eb",
            boxShadow: "0 6px 20px rgba(37, 99, 235, 0.4)",
            fontWeight: 700,
            fontSize: "14px",
            height: "46px",
            padding: "0 22px",
          }}
        >
          Become a Partner
        </Button>
      </Affix>

      <LandingNavbar />
      <main id="main-content">
        <LandingHero />
        <TrustStrip />
        <OptionCards />
        <ServicesSection />
        <Suspense fallback={null}>
          <Reveal><CityGrid /></Reveal>
          <Reveal><HowItWorks /></Reveal>
          <Reveal><AppPromo /></Reveal>
          <Reveal><BlogSection /></Reveal>
          <Reveal><SocialCards /></Reveal>
          <Reveal><CtaBanner /></Reveal>
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <LandingFooter />
      </Suspense>
    </div>
  );
}

export default HomePage;
